import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { fetchPersonSubscriptions } from '@/api/subscriptions'
import { clientCardPollingInterval } from '@/config/api'
import { useDrainPages } from '@/composables/useDrainPages'
import { useSubscriptionTypeCatalog } from '@/composables/useSubscriptionTypeCatalog'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const MS_PER_DAY = 86_400_000

export interface ClientSubscription {
  // A subscription bought by the client, joined with its catalog plan; what the
  // client card shows in the active-subscriptions list and ledger marks.

  id: string
  // Subscription id (UUID7); matches `subscription_id` on visits and payments.

  typeId: string
  // Catalog plan id.

  name: string
  // Plan name; empty when the viewer cannot read the catalog.

  validFrom: string
  // ISO datetime from which the subscription is valid.

  validUntil: string | null
  // ISO datetime the subscription ends (`validFrom` + plan duration); null
  // when the plan is unknown.

  total: number | null
  // Visit quota of the plan; null when unlimited (or the plan is unknown).

  price: number | null
  // Catalog price the balance is charged when the subscription is exhausted or
  // expires; null when the plan or its price is unknown.

  used: number | null
  // Visits spent so far (`total - remaining`); null when unlimited.

  isExhausted: boolean
  // The visit quota is used up.

  isActive: boolean
  // The backend still treats the subscription as active.
}

/**
 * The client's purchased subscriptions for the client card. Reads
 * `GET /subscriptions/clients/{id}` (drained) when the viewer holds
 * `subscriptions_view` and joins each item to the shared plan catalog for the
 * name, end date and visit quota. `isLoading` covers the first fetch so the
 * card can tell "none yet" from "not loaded".
 */
export function useClientSubscriptions(clientId: MaybeRefOrGetter<string | null | undefined>): {
  subscriptions: ComputedRef<ClientSubscription[]>
  canView: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
} {
  const { hasRole } = usePermissions()
  const canView = computed(() => hasRole(ResourceAccessRole.SubscriptionsView))
  const { types } = useSubscriptionTypeCatalog()

  const query = useInfiniteQuery({
    queryKey: ['subscriptions', 'client', computed(() => toValue(clientId))],
    queryFn: ({ pageParam }) => fetchPersonSubscriptions(toValue(clientId)!, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: computed(() => canView.value && !!toValue(clientId)),
    staleTime: clientCardPollingInterval,
    refetchInterval: clientCardPollingInterval,
    refetchIntervalInBackground: false,
  })
  useDrainPages(query)

  const subscriptions = computed<ClientSubscription[]>(() => {
    const typeById = new Map(types.value.map((type) => [type.id, type]))
    return (query.data.value?.pages ?? [])
      .flatMap((page) => page.items ?? [])
      .map((sub) => {
        const type = typeById.get(sub.subscription_type_id)
        const total = type?.number_of_services ?? null
        const until = type
          ? new Date(new Date(sub.valid_from).getTime() + type.duration * MS_PER_DAY)
          : null
        return {
          id: sub.id,
          typeId: sub.subscription_type_id,
          name: type?.name ?? '',
          validFrom: sub.valid_from,
          validUntil: until && !Number.isNaN(until.getTime()) ? until.toISOString() : null,
          total,
          price: type?.price?.price ?? null,
          used:
            total !== null && sub.remaining !== null ? Math.max(total - sub.remaining, 0) : null,
          isExhausted: sub.is_exhausted,
          isActive: sub.is_active,
        }
      })
  })

  const isLoading = computed(() => canView.value && !!toValue(clientId) && query.isPending.value)

  return { subscriptions, canView, isLoading }
}
